/** Runtime environment-config RESOLUTION for the Next apps and the browser bundle: `process.env` server-side,
 * the shell-injected `window.__ENV_CONFIG__` client-side.
 *
 * IT LIVES IN `components/lib/`, the sanctioned non-presentational DI/transport subtree (a `QUAL-COMP-PURE-001`
 * excludeGlob, alongside `resolve-env.ts`), because both of its audiences are platform runtimes this tier already
 * serves -- React providers/hooks on the client and Next server code on the server. It is not a CLI tool, so `tools/`
 * is no home for it: every one of its importers is a component, a hook, a Next route or an app layout. It cannot move
 * DOWN to the utils floor either: it value-imports two utils siblings
 * (`config/env-config`, `async/degraded`) and `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids a utils-to-utils value
 * import -- inlining them to force purity would only duplicate the pure helpers.
 *
 * The pure `isNodeDevelopment()` predicate that used to live here moved to `utils/config/env-config.ts` beside the
 * `resolveEnvironment()` it delegates to. For Cloudflare Worker handlers, use `tools/config/env.ts` instead. */
import { getEnv, resolveEnvironment } from '@federa/utils/config/env-config';
import { ENVIRONMENTS } from '@federa/const/config-environments';
import { BaseError } from '@federa/const/base-error';
import { ERROR_CODES } from '@federa/const/error-codes';
import { okOutcome, degradedOutcome } from '@federa/utils/async/degraded';
import { FIREBASE_PROJECT_ID_DEV, DATABASE_NAMES_EXTENDED } from '@federa/const/database-names';
import { CRITICAL_ENV_CONFIG_FIELDS, ENV_VAR_CONFIG_MAP, OPTIONAL_ENV_CONFIG_FIELDS } from '@federa/const/env-config-field-map';
import { NEXT_BUILD_PHASE } from '@federa/const/env-var-names-app-config';
import { RUNTIME_CONFIG_DEGRADED_REASONS } from '@federa/const/runtime-config-degraded-reasons';
import type { EnvironmentConfig } from '@federa/types/frontend-runtime-config';
import type { Outcome } from '@federa/types/observability-outcome';

declare const window: undefined | { __ENV_CONFIG__?: EnvironmentConfig };
declare global {
	interface Window {
		__ENV_CONFIG__?: EnvironmentConfig;
	}
}

// Server-side config builder

function collectDevValueLeaks(config: EnvironmentConfig): string[] {
	const leaks: string[] = [];
	if (config.storageGatewayApi.includes('-dev.')) leaks.push(`storageGatewayApi has development value "${config.storageGatewayApi}"`);
	if (config.firebaseProjectId === FIREBASE_PROJECT_ID_DEV)
		leaks.push(`firebaseProjectId has development value "${config.firebaseProjectId}"`);
	if (config.stripePublicKey.startsWith('pk_test_')) leaks.push(`stripePublicKey has development value "${config.stripePublicKey}"`);
	if (config.databaseName === DATABASE_NAMES_EXTENDED.GATEWAY_DEV)
		leaks.push(`databaseName has development value "${config.databaseName}"`);
	return leaks;
}

function collectMissingCritical(config: EnvironmentConfig): string[] {
	return CRITICAL_ENV_CONFIG_FIELDS.filter((f) => !config[f]).map((f) => `${f} is empty or missing`);
}

/** Collect optional fields that are empty (feature-specific; degrade, don't block). */
function collectEmptyOptional(config: EnvironmentConfig): Array<keyof EnvironmentConfig> {
	return OPTIONAL_ENV_CONFIG_FIELDS.filter((field) => !config[field]);
}

/** Validate production config. Throws on critical-field integrity failures; returns the list of empty
 * optional fields so the caller can surface them as a degraded outcome rather than a warning. */
function validateProdConfig(config: EnvironmentConfig): Array<keyof EnvironmentConfig> {
	const failures = [...collectDevValueLeaks(config), ...collectMissingCritical(config)];
	if (failures.length > 0) {
		throw new BaseError({
			code: ERROR_CODES.CONFIG_ENV_MISSING,
			message: `[buildServerConfig] PRODUCTION CONFIG INTEGRITY FAILURE: ${failures.join('; ')}. Check wrangler.jsonc NEXT_PUBLIC_* vars.`,
			details: { failureCount: failures.length },
		});
	}
	return collectEmptyOptional(config);
}

/** Build the server-side environment config from NEXT_PUBLIC env vars. In production it throws on
 * critical-field integrity failures and resolves degraded
 * (`RUNTIME_CONFIG_DEGRADED_REASONS.OPTIONAL_CONFIG_FIELDS_EMPTY`) when empty.
 *
 * NOT DURING `next build`. The build runs with NODE_ENV=production and binds no runtime var -- a deployed Worker binds
 * them from its wrangler vars -- so a prerender resolves PRODUCTION with every critical field empty, by design. The
 * integrity check guards the LIVE render; at build it answers the unbound config as `ENV_CONFIG_UNAVAILABLE`, the same
 * reason the client-safe projection falls back on, and the deployed render still checks. */
export function buildServerConfig(): Outcome<EnvironmentConfig> {
	const config = { storageGatewayApiToken: undefined } as EnvironmentConfig;
	for (const [key, envVar] of ENV_VAR_CONFIG_MAP) {
		config[key] = getEnv([envVar], '');
	}
	if (resolveEnvironment() !== ENVIRONMENTS.PRODUCTION) return okOutcome(config);
	if (getEnv([NEXT_BUILD_PHASE.ENV_KEY], '') === NEXT_BUILD_PHASE.PRODUCTION_BUILD) {
		return degradedOutcome(config, RUNTIME_CONFIG_DEGRADED_REASONS.ENV_CONFIG_UNAVAILABLE);
	}
	const emptyOptionalFields = validateProdConfig(config);
	if (emptyOptionalFields.length > 0) {
		return degradedOutcome(config, RUNTIME_CONFIG_DEGRADED_REASONS.OPTIONAL_CONFIG_FIELDS_EMPTY, { emptyOptionalFields });
	}
	return okOutcome(config);
}

// Server / client environment detection

let cachedClientConfig: EnvironmentConfig | null = null;

export function getEnvironmentConfig(): EnvironmentConfig {
	if (typeof window !== 'undefined') {
		if (cachedClientConfig) return cachedClientConfig;
		const runtimeConfig = window.__ENV_CONFIG__;
		if (runtimeConfig) {
			cachedClientConfig = runtimeConfig;
			return runtimeConfig;
		}
		throw new BaseError({
			code: ERROR_CODES.CONFIG_ENV_MISSING,
			message:
				'[environment] window.__ENV_CONFIG__ not injected. ' +
				'The app must inject runtime config before shared utilities are called. ' +
				'Check that EnvConfigScript runs in the root layout.',
		});
	}
	return buildServerConfig().value;
}

/** Whether this page or server render belongs to the production fleet -- the ONE spelling of that question for the
 *  Next apps and the browser bundle. Throws where {@link getEnvironmentConfig} does: a page whose layout injected no
 *  runtime config cannot say which fleet it is in, and a caller that must answer anyway catches. */
export function isProductionEnvironment(): boolean {
	return getEnvironmentConfig().environment === ENVIRONMENTS.PRODUCTION;
}
